/**
 * features/terms/tabs/DeadlinesTab.tsx
 * ------------------------------------------------------------------
 * Per-term deadlines + month locks + parent notifications.
 *
 *   - Mark-entry deadlines (Round 4): set per-CA and term-final
 *     calendar dates. Scheduled Cloud Function posts 7d/3d/1d/
 *     day-of warnings and auto-locks the month after each deadline
 *     passes.
 *
 *   - Month locks (P6): admin can manually lock a CA month at any
 *     time, independently of any deadline.
 *
 *   - Parent notifications (Round 11): per-CA date for one bulk
 *     notification to parents "new marks are in, log in to see".
 *     Defaults to deadline + 1 day. Scheduled Cloud Function
 *     `processParentCaNotifications` runs daily and fires the
 *     notification on its date; admin can also click "Send now".
 *     Sent state is recorded on the term doc so it only fires once.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { doc } from 'firebase/firestore';
// Batch C: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { httpsCallable } from 'firebase/functions';
import {
  CalendarDays,
  Lock,
  Unlock,
  AlertTriangle,
  Save,
  Check,
  Send,
  Mail,
  Loader2,
} from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { TermKey } from '@/lib/school.config';
import { getCaLabels } from '@/lib/assessmentConfig';
import { Card } from '@/components/ui/primitives';
import type { MarkEntryDeadlines, TermState } from '@/types';

interface Props {
  year: string;
  term: TermKey;
}

export default function DeadlinesTab({ year, term }: Props) {
  const { termStatuses, assessmentConfigs } = useData();
  const { userData } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [deadlineDraft, setDeadlineDraft] = useState<MarkEntryDeadlines | null>(null);
  // Round 12: publish/unpublish modal state.
  const [unpublishTarget, setUnpublishTarget] = useState<
    { kind: 'ca'; caLabel: string } | { kind: 'final' } | null
  >(null);
  const [unpublishReason, setUnpublishReason] = useState('');

  const status = termStatuses.find((t) => t.year === year && t.term === term);
  const termState: TermState = status?.status ?? 'open';
  const caLabels = getCaLabels(term, assessmentConfigs);

  const savedDeadlines: MarkEntryDeadlines = status?.deadlines ?? {};
  const monthLocks: Record<string, boolean> = status?.monthLocks ?? {};
  // Round 12: publish state derived from termStatus
  const publishedCas: Record<string, { at: string; by: string }> =
    status?.publishedCas ?? {};
  const publishedTermFinal = status?.publishedTermFinal;

  const deadlines = deadlineDraft ?? savedDeadlines;
  const dirty =
    deadlineDraft !== null &&
    JSON.stringify(deadlineDraft) !== JSON.stringify(savedDeadlines);

  /** Update a CA deadline OR the term-final deadline. */
  function updateDeadline(field: 'termDeadline' | string, value: string) {
    const cur = deadlines;
    if (field === 'termDeadline') {
      setDeadlineDraft({ ...cur, termDeadline: value || undefined });
      return;
    }
    const ca = { ...(cur.ca ?? {}) };
    if (value) ca[field] = value;
    else delete ca[field];
    setDeadlineDraft({ ...cur, ca });
  }

  async function saveDeadlines() {
    if (!deadlineDraft) return;
    setBusy('deadlines');
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: 'Saving deadlines…' });
    try {
      const id = `${year}_${term}`;
      await setDoc(
        doc(db, COLLECTIONS.termStatus, id),
        {
          id,
          year,
          term,
          deadlines: deadlineDraft,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid,
        },
        { merge: true },
      );
      setDeadlineDraft(null);
      setSaveModal({ show: true, status: 'success', message: 'Deadlines saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function toggleMonthLock(monthLabel: string) {
    setBusy(monthLabel);
    setMsg(null);
    try {
      const id = `${year}_${term}`;
      const next = { ...monthLocks, [monthLabel]: !monthLocks[monthLabel] };
      await setDoc(
        doc(db, COLLECTIONS.termStatus, id),
        {
          id,
          year,
          term,
          monthLocks: next,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid,
        },
        { merge: true },
      );
      setMsg(`${monthLabel} ${next[monthLabel] ? 'locked' : 'unlocked'}.`);
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Trigger the parent-notification for a specific CA NOW (admin override). */
  /* ===================================================================
     Round 12 — publish/unpublish handlers
     =================================================================== */

  /** Publish a CA. Cloud Function writes the timestamp AND fires the
   *  parent notification in a single transaction. */
  async function publishCa(caLabel: string) {
    setBusy(`publish:${caLabel}`);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'publishCa');
      const res = (await fn({ year, term, caLabel })) as {
        data?: { ok?: boolean; recipients?: number; error?: string };
      };
      const d = res.data ?? {};
      if (d.ok) {
        setMsg(
          `Published ${caLabel}. Notification sent to ${d.recipients ?? 0} parent${
            d.recipients === 1 ? '' : 's'
          }.`,
        );
      } else {
        setMsg(d.error ?? 'Could not publish.');
      }
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Publish the term-final exam marks. */
  async function publishTermFinal() {
    setBusy('publish:final');
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'publishTermFinal');
      const res = (await fn({ year, term })) as {
        data?: { ok?: boolean; recipients?: number; error?: string };
      };
      const d = res.data ?? {};
      if (d.ok) {
        setMsg(
          `Published the term-final. Notification sent to ${d.recipients ?? 0} parent${
            d.recipients === 1 ? '' : 's'
          }.`,
        );
      } else {
        setMsg(d.error ?? 'Could not publish.');
      }
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Open the unpublish confirm modal for a CA. */
  function askUnpublishCa(caLabel: string) {
    setUnpublishTarget({ kind: 'ca', caLabel });
    setUnpublishReason('');
  }

  /** Open the unpublish confirm modal for the term-final. */
  function askUnpublishTermFinal() {
    setUnpublishTarget({ kind: 'final' });
    setUnpublishReason('');
  }

  /** Execute the unpublish action chosen in the modal. */
  async function confirmUnpublish() {
    if (!unpublishTarget) return;
    const reason = unpublishReason.trim();
    if (!reason) return;
    if (unpublishTarget.kind === 'ca') {
      const caLabel = unpublishTarget.caLabel;
      setBusy(`unpublish:${caLabel}`);
      try {
        const fn = httpsCallable(functions, 'unpublishCa');
        const res = (await fn({ year, term, caLabel, reason })) as {
          data?: { ok?: boolean; error?: string };
        };
        const d = res.data ?? {};
        setMsg(d.ok ? `Unpublished ${caLabel}.` : d.error ?? 'Could not unpublish.');
      } catch (e) {
        setMsg((e as Error).message);
      } finally {
        setBusy(null);
        setUnpublishTarget(null);
        setUnpublishReason('');
      }
    } else {
      setBusy('unpublish:final');
      try {
        const fn = httpsCallable(functions, 'unpublishTermFinal');
        const res = (await fn({ year, term, reason })) as {
          data?: { ok?: boolean; error?: string };
        };
        const d = res.data ?? {};
        setMsg(d.ok ? 'Unpublished the term-final.' : d.error ?? 'Could not unpublish.');
      } catch (e) {
        setMsg((e as Error).message);
      } finally {
        setBusy(null);
        setUnpublishTarget(null);
        setUnpublishReason('');
      }
    }
  }

  function daysFromNow(iso: string | undefined): string {
    if (!iso) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(iso + 'T00:00:00');
    const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diff < 0) return `${-diff} days ago`;
    if (diff === 0) return 'today';
    if (diff === 1) return 'tomorrow';
    return `in ${diff} days`;
  }

  function formatSentDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  }

  return (
    <div className="space-y-4">
      {msg && (
        <div className="text-sm rounded-xl px-3 py-2.5 border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 flex items-center gap-2">
          <Check size={15} /> {msg}
        </div>
      )}

      {/* Mark-entry deadlines */}
      <Card className="p-5">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <CalendarDays size={15} className="text-primary" />
          Mark entry deadlines
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Set a deadline date for each assessment. Reminders post automatically at 7, 3 and 1 day before, and on the day. The month auto-locks the day after the deadline.
        </p>

        <div className="space-y-2 max-w-md">
          {caLabels.length === 0 && (
            <p className="text-xs text-gray-400">
              No CAs configured for this term yet. Set them up in the Assessments tab first.
            </p>
          )}
          {caLabels.map((label) => {
            const dateValue = deadlines.ca?.[label] ?? '';
            return (
              <div key={label} className="flex items-center gap-2 text-sm">
                <span className="w-14 shrink-0 font-medium text-gray-600 dark:text-gray-300">
                  {label}
                </span>
                <input
                  type="date"
                  value={dateValue}
                  onChange={(e) => updateDeadline(label, e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm focus:outline-none focus:border-primary"
                />
                {dateValue && (
                  <span className="text-[10px] text-gray-400 shrink-0 w-20 text-right">
                    {daysFromNow(dateValue)}
                  </span>
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-2 text-sm pt-2 border-t border-dashed border-gray-200 dark:border-gray-700 mt-3">
            <span className="w-14 shrink-0 font-semibold text-primary text-xs">
              FINAL
            </span>
            <input
              type="date"
              value={deadlines.termDeadline ?? ''}
              onChange={(e) => updateDeadline('termDeadline', e.target.value)}
              className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border-2 border-primary/30 dark:border-primary/50 dark:bg-gray-800 text-sm focus:outline-none focus:border-primary"
            />
            {deadlines.termDeadline && (
              <span className="text-[10px] text-gray-400 shrink-0 w-20 text-right">
                {daysFromNow(deadlines.termDeadline)}
              </span>
            )}
          </div>
        </div>

        {dirty && (
          <div className="mt-4 flex items-center justify-between gap-2 max-w-md">
            <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle size={12} /> Unsaved changes
            </span>
            <button
              onClick={saveDeadlines}
              disabled={busy !== null}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              <Save size={12} /> Save deadlines
            </button>
          </div>
        )}
      </Card>

      {/* Round 12: Publish to parents */}
      <Card className="p-5">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <Mail size={15} className="text-primary" />
          Publish to parents
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Each CA stays hidden from parents until you publish it.
          Publishing makes the marks visible in the parent portal and
          sends one notification (in-app + email) telling parents to
          log in. The term-final exam has its own publish toggle for
          when the end-of-term exam is fully reviewed.
        </p>

        <div className="space-y-2 max-w-2xl">
          {caLabels.length === 0 && (
            <p className="text-xs text-gray-400">
              No CAs configured for this term yet. Set them up in the
              Assessments tab first.
            </p>
          )}
          {caLabels.map((label) => {
            const published = publishedCas[label];
            const isWorking = busy === `publish:${label}` || busy === `unpublish:${label}`;
            return (
              <div
                key={label}
                className="flex items-center gap-2 text-sm flex-wrap"
              >
                <span className="w-14 shrink-0 font-medium text-gray-600 dark:text-gray-300">
                  {label}
                </span>
                {published ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700">
                      <Check size={11} />
                      Published {formatSentDate(published.at)}
                    </span>
                    <span className="flex-1 text-[11px] text-gray-400 italic">
                      Parents can see {label} marks in the portal.
                    </span>
                    <button
                      onClick={() => askUnpublishCa(label)}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20 disabled:opacity-40"
                    >
                      {isWorking ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : null}
                      Unpublish
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-[11px] text-gray-400 italic">
                      Hidden from parents.
                    </span>
                    <button
                      onClick={() => publishCa(label)}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-primary text-primary hover:bg-primary/5 disabled:opacity-40"
                    >
                      {isWorking ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Send size={11} />
                      )}
                      Publish {label} & notify parents
                    </button>
                  </>
                )}
              </div>
            );
          })}

          {/* Term-final row */}
          <div className="border-t border-gray-100 dark:border-gray-700 mt-3 pt-3">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="w-14 shrink-0 font-medium text-gray-600 dark:text-gray-300">
                Final
              </span>
              {publishedTermFinal ? (
                <>
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700">
                    <Check size={11} />
                    Published {formatSentDate(publishedTermFinal.at)}
                  </span>
                  <span className="flex-1 text-[11px] text-gray-400 italic">
                    Term-final exam marks visible to parents.
                  </span>
                  <button
                    onClick={() => askUnpublishTermFinal()}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20 disabled:opacity-40"
                  >
                    {busy === 'unpublish:final' ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : null}
                    Unpublish
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-[11px] text-gray-400 italic">
                    Term-final exam marks hidden from parents.
                  </span>
                  <button
                    onClick={() => publishTermFinal()}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-primary text-primary hover:bg-primary/5 disabled:opacity-40"
                  >
                    {busy === 'publish:final' ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <Send size={11} />
                    )}
                    Publish term-final & notify parents
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Round 12: Unpublish confirm modal */}
      {unpublishTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setUnpublishTarget(null)}
        >
          <div
            className="w-full max-w-md p-6 bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border border-white/50 dark:border-white/10 ring-1 ring-black/5 rounded-2xl shadow-2xl"
            onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Unpublish{' '}
              {unpublishTarget.kind === 'ca'
                ? `CA ${unpublishTarget.caLabel}`
                : 'the term-final'}?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Parents will no longer see these marks in the portal.
              No notification is sent (a &ldquo;marks were hidden&rdquo;
              email would be confusing). The unpublish is recorded in
              the audit log with your reason.
            </p>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Why are you unpublishing?
            </label>
            <input
              type="text"
              value={unpublishReason}
              onChange={(e) => setUnpublishReason(e.target.value)}
              placeholder="e.g. discovered marks-entry error, will republish after fix"
              className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none text-sm mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setUnpublishTarget(null);
                  setUnpublishReason('');
                }}
                className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmUnpublish()}
                disabled={!unpublishReason.trim() || busy !== null}
                className="px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
              >
                Unpublish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month locks */}
      <Card className="p-5">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <Lock size={15} className="text-primary" />
          Month locks
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          A locked month blocks mark edits for that month even while the term is open. Months auto-lock the day after their deadline passes (Round 4) — use this to manually lock early or to extend a passed deadline (unlock + bump the date above).
        </p>

        {termState !== 'open' ? (
          <p className="text-xs text-gray-400 italic">
            Term is currently <strong>{termState}</strong> — all months are already locked by the term status.
          </p>
        ) : caLabels.length === 0 ? (
          <p className="text-xs text-gray-400">
            No CAs configured for this term yet. Set them up in the Assessments tab first.
          </p>
        ) : (
          <div className="space-y-1.5 max-w-md">
            {caLabels.map((m) => {
              const locked = !!monthLocks[m];
              return (
                <button
                  key={m}
                  disabled={busy !== null}
                  onClick={() => toggleMonthLock(m)}
                  className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-all ${
                    locked
                      ? 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  } disabled:opacity-50`}
                >
                  <span>{m}</span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold">
                    {locked ? (
                      <>
                        <Lock size={11} /> Locked
                      </>
                    ) : (
                      <>
                        <Unlock size={11} /> Open
                      </>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
      </Card>
    </div>
  );
}
